import * as React from "react"

import { cn } from "@/lib/utils"

type CardVariant = "default" | "glass" | "outlined" | "elevated" | "interactive"

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant
  interactive?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = "default", interactive = false, ...props }, ref) => {
    const variants = {
      default: "rounded-2xl border border-border bg-card text-card-foreground shadow-soft",
      glass: "rounded-2xl border border-border bg-card/80 text-card-foreground backdrop-blur-md shadow-glass",
      outlined: "rounded-2xl border border-border bg-card text-card-foreground",
      elevated: "rounded-2xl bg-card text-card-foreground shadow-card",
      interactive: "rounded-2xl border border-border bg-card text-card-foreground shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-primary/25 hover:shadow-card-hover"
    }

    const interactiveClasses = interactive 
      ? "cursor-pointer active:scale-[0.99] select-none" 
      : ""

    return (
      <div
        ref={ref}
        className={cn(
          variants[variant],
          interactive && variant !== "interactive" ? "cursor-pointer active:scale-[0.99] transition-all duration-300 hover:-translate-y-1 hover:border-primary/25 hover:shadow-card-hover" : "",
          interactiveClasses,
          className
        )}
        {...props}
      />
    )
  }
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-bold tracking-tight text-card-foreground", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm font-medium text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
